import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Gift, Heart, Send, Users, Diamond, CheckCircle, 
  Clock, Mail, Sparkles, ArrowRight, Star, Loader2, Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI-suggested messages that users can customize
const SUGGESTED_MESSAGES = [
  {
    personal_message: "I've been part of this incredible community of women, and it has truly transformed how I show up for myself. I immediately thought of you because I know you'd thrive here. This is a space where you can exhale, be seen, and be supported by women who truly understand.",
    why_gifting: "Because you deserve a sanctuary where you don't have to carry it all alone. This community will pour into you the way you pour into everyone else."
  },
  {
    personal_message: "Sis, I wanted to share something special with you. This community has become my sacred space - a place where I can be real, be held, and be challenged to grow. I know you've been carrying a lot, and I want you to experience this sisterhood too.",
    why_gifting: "Because even the strongest women need a soft place to land. You give so much to others - this is a space where you can receive."
  },
  {
    personal_message: "I've found my tribe in this community, and I couldn't help but think of you. These are high-capacity women who understand the unique challenges we face. No judgment, just genuine support and wisdom.",
    why_gifting: "Because you're the kind of woman who lifts others up, and you deserve to be surrounded by women who will lift you too."
  }
];

const GiftMembershipPage = () => {
  const { user } = useAuth();
  const { grandCelebrate } = useCelebration();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [myGifts, setMyGifts] = useState(null);
  const [loadingGifts, setLoadingGifts] = useState(true);
  const [suggestedIndex, setSuggestedIndex] = useState(0);
  
  const [formData, setFormData] = useState({
    recipient_name: '',
    recipient_email: '',
    personal_message: SUGGESTED_MESSAGES[0].personal_message,
    why_gifting: SUGGESTED_MESSAGES[0].why_gifting
  });

  useEffect(() => {
    fetchMyGifts();
  }, []);

  const fetchMyGifts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/my-gifts`);
      setMyGifts(response.data);
    } catch (error) {
      console.error('Error fetching gifts:', error);
    } finally {
      setLoadingGifts(false);
    }
  };

  const cycleSuggestion = () => {
    const nextIndex = (suggestedIndex + 1) % SUGGESTED_MESSAGES.length;
    setSuggestedIndex(nextIndex);
    setFormData({
      ...formData,
      personal_message: SUGGESTED_MESSAGES[nextIndex].personal_message,
      why_gifting: SUGGESTED_MESSAGES[nextIndex].why_gifting
    });
    toast.success('New suggested message loaded - feel free to customize!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_name || !formData.recipient_email || !formData.personal_message || !formData.why_gifting) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/gift/membership`, formData);
      
      setSent(true);
      grandCelebrate("Your gift of sisterhood is on its way! 💝");
      toast.success(response.data.message);
      
      // Refresh gifts list
      fetchMyGifts();
      
      // Reset form with new suggestion
      const nextIndex = (suggestedIndex + 1) % SUGGESTED_MESSAGES.length;
      setSuggestedIndex(nextIndex);
      setFormData({
        recipient_name: '',
        recipient_email: '',
        personal_message: SUGGESTED_MESSAGES[nextIndex].personal_message,
        why_gifting: SUGGESTED_MESSAGES[nextIndex].why_gifting
      });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send gift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 mb-4">
            <Gift className="h-8 w-8 text-pink-600" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-2">
            Gift the Sisterhood
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Know someone who needs this community? Gift them a free month and earn 
            <span className="font-semibold text-purple-600"> 100 diamond points ($5 value)</span> when they join!
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Gift Form */}
          <div className="lg:col-span-2">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                  Send a Gift Membership
                </CardTitle>
                <CardDescription>
                  Your friend will receive a personalized email with your message
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sent ? (
                  <div className="text-center py-8">
                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl text-forest mb-2">Gift Sent Successfully!</h3>
                    <p className="text-gray-600 mb-6">
                      Your gift invitation has been sent. You'll earn 100 diamonds when they accept!
                    </p>
                    <Button 
                      onClick={() => setSent(false)}
                      className="btn-primary"
                    >
                      Send Another Gift
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* AI Suggestion Banner */}
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2">
                        <Wand2 className="h-4 w-4 text-purple-600" />
                        <span className="text-sm text-purple-700">
                          <strong>AI-suggested message</strong> - customize it to make it yours!
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cycleSuggestion}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Try Another
                      </Button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="recipient_name">Their Name</Label>
                        <Input
                          id="recipient_name"
                          value={formData.recipient_name}
                          onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                          placeholder="Friend's name"
                          className="input-base mt-1"
                          data-testid="gift-recipient-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="recipient_email">Their Email</Label>
                        <Input
                          id="recipient_email"
                          type="email"
                          value={formData.recipient_email}
                          onChange={(e) => setFormData({...formData, recipient_email: e.target.value})}
                          placeholder="friend@email.com"
                          className="input-base mt-1"
                          data-testid="gift-recipient-email"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="personal_message">Your Personal Message</Label>
                        <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                          Editable
                        </Badge>
                      </div>
                      <Textarea
                        id="personal_message"
                        value={formData.personal_message}
                        onChange={(e) => setFormData({...formData, personal_message: e.target.value})}
                        placeholder="Write a heartfelt message to your friend..."
                        className="input-base mt-1 min-h-[120px]"
                        data-testid="gift-personal-message"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This will appear in their email - feel free to personalize!
                      </p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="why_gifting">Why You're Gifting This</Label>
                        <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                          Editable
                        </Badge>
                      </div>
                      <Textarea
                        id="why_gifting"
                        value={formData.why_gifting}
                        onChange={(e) => setFormData({...formData, why_gifting: e.target.value})}
                        placeholder="I'm gifting this because I think you would really benefit from..."
                        className="input-base mt-1 min-h-[80px]"
                        data-testid="gift-why-gifting"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Help them understand why you chose this gift for them
                      </p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                      disabled={loading}
                      data-testid="send-gift-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending Gift...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Gift Invitation
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* What They Get */}
            <Card className="card-base bg-gradient-to-br from-sand/50 to-white">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  What They'll Receive
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>30 days FREE</strong> Inner Circle membership</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access to all sister circles & sacred spaces</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Weekly transmissions & reflection prompts</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Connect with high-capacity women</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>No credit card required to start</span>
                </div>
              </CardContent>
            </Card>

            {/* Your Reward */}
            <Card className="card-base border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Diamond className="h-5 w-5 text-purple-600" />
                  Your Reward
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600 mb-1">100</p>
                  <p className="text-sm text-purple-700">Diamond Points</p>
                  <p className="text-xs text-gray-500 mt-2">= $5 toward swag, coaching, or programs</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* My Referrals Section */}
        {myGifts && (
          <div className="mt-8">
            <h2 className="font-heading text-xl text-forest mb-4">Your Gift Activity</h2>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-forest">{myGifts.pending_gifts?.length || 0}</p>
                  <p className="text-sm text-gray-500">Pending Invitations</p>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-green-600">{myGifts.successful_referrals?.length || 0}</p>
                  <p className="text-sm text-gray-500">Successful Referrals</p>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-purple-600">{myGifts.total_referral_points_earned || 0}</p>
                  <p className="text-sm text-gray-500">Points Earned from Referrals</p>
                </CardContent>
              </Card>
            </div>

            {/* Pending Gifts */}
            {myGifts.pending_gifts?.length > 0 && (
              <Card className="card-base mb-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Pending Invitations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myGifts.pending_gifts.filter(g => g.status === 'pending').map((gift) => (
                      <div key={gift.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium text-forest">{gift.recipient_name}</p>
                          <p className="text-sm text-gray-500">{gift.recipient_email}</p>
                        </div>
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Awaiting Response
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Successful Referrals */}
            {myGifts.successful_referrals?.length > 0 && (
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-green-500" />
                    Successful Referrals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myGifts.successful_referrals.map((referral) => (
                      <div key={referral.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium text-forest">{referral.referred_name}</p>
                          <p className="text-sm text-gray-500">
                            Joined {new Date(referral.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">
                          +{referral.points_awarded} points
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GiftMembershipPage;
